import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { useTutorialStore } from "@/store/tutorial";
import { FEATURES } from "@/config/features";

// Views
import Home from "@/views/Home.vue";
import Login from "@/views/auth/Login.vue";
import Register from "@/views/auth/Register.vue";
import EmailVerification from "@/views/auth/EmailVerification.vue";
import Profile from "@/views/Profile.vue";
import PostDetail from "@/views/PostDetail.vue";
import SavedPosts from "@/views/SavedPosts.vue";
import AdminDashboard from "@/views/admin/Dashboard.vue";
import AdminCreatePost from "@/views/admin/CreatePost.vue";
import ForgotPassword from "@/views/auth/ForgotPassword.vue";
import ResetPassword from "@/views/auth/ResetPassword.vue";
import NotFound from "@/views/NotFound.vue";

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { requiresGuest: true },
  },
  {
    path: "/register",
    name: "Register",
    component: Register,
    meta: { requiresGuest: true },
  },
  {
    path: "/auth/verify/:token",
    name: "EmailVerification",
    component: EmailVerification,
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: { requiresGuest: true },
  },
  {
    path: "/auth/reset-password/:token",
    name: "ResetPassword",
    component: ResetPassword,
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: "/posts/:id",
    name: "PostDetail",
    component: PostDetail,
    props: true,
  },
  {
    path: "/saved",
    name: "SavedPosts",
    component: SavedPosts,
    meta: { requiresAuth: true },
  },
  {
    path: "/admin",
    name: "AdminDashboard",
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/create-post",
    name: "AdminCreatePost",
    component: AdminCreatePost,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check if user is authenticated
  const isAuthenticated = authStore.isAuthenticated;
  const isAdmin = authStore.isAdmin;

  // Routes that require authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: "Login", query: { redirect: to.fullPath } });
    return;
  }

  // Routes that require admin role
  if (to.meta.requiresAdmin && !isAdmin) {
    next({ name: "Home" });
    return;
  }

  // Routes that require guest (not authenticated)
  if (to.meta.requiresGuest && isAuthenticated) {
    next({ name: "Home" });
    return;
  }

  next();
});

// After navigation - trigger tutorial for new users
router.afterEach((to, from) => {
  const authStore = useAuthStore();
  const tutorialStore = useTutorialStore();

  console.log("🔍 Tutorial Check:", {
    toName: to.name,
    fromName: from.name,
    isAuthenticated: authStore.isAuthenticated,
    userExists: !!authStore.user,
    tutorial_completed: authStore.user?.tutorial_completed,
    email_verified: authStore.user?.email_verified,
    waitingForCommentPage: tutorialStore.waitingForCommentPage,
  });

  // SE está aguardando página de comentário e chegou em PostDetail
  if (to.name === "PostDetail" && tutorialStore.waitingForCommentPage) {
    console.log(
      "📄 Navegou para PostDetail! Executando auto-scroll em 1.5s..."
    );
    // Aguardar DOM carregar completamente (posts, comentários, etc)
    setTimeout(() => {
      tutorialStore.handleCommentPageLoaded();
    }, 1500);
  }

  // Iniciar tutorial se:
  // 1. Tutorial está HABILITADO na configuração
  // 2. Usuário está autenticado
  // 3. Não completou tutorial
  // 4. Está na página Home
  // 5. NÃO está fazendo refresh na mesma página
  if (
    FEATURES.ENABLE_TUTORIAL &&
    to.name === "Home" &&
    authStore.isAuthenticated &&
    !authStore.user?.tutorial_completed &&
    from.name !== to.name // Permite from.name undefined (primeira carga)
  ) {
    console.log(
      "✅ Todas condições atendidas! Iniciando tutorial em 1000ms..."
    );
    console.log(`🎓 Tutorial habilitado: ${FEATURES.ENABLE_TUTORIAL}`);
    // Aguardar 1000ms para garantir que posts foram carregados
    setTimeout(() => {
      console.log("🎓 Chamando tutorialStore.startTutorial()");
      tutorialStore.startTutorial();
    }, 1000);
  } else if (!FEATURES.ENABLE_TUTORIAL && to.name === "Home") {
    console.log("⚠️ Tutorial DESABILITADO via FEATURES.ENABLE_TUTORIAL");
  }
});

export default router;
